import { defineStore } from 'pinia'
import http, { getStoredToken, setStoredToken, clearStoredToken } from '../services/http'

export interface AuthUserRole {
  id: number
  name: string
  // Permission strings (e.g. "providers.create"), or ["*"] for the admin role (full access).
  // Read via the usePermissions() composable rather than accessed directly, so every screen
  // checks permissions the same way.
  permissions: string[]
}

export interface AuthUser {
  id: number | string
  name: string
  email: string
  // The API returns the role as an object (same `{ id, name }` shape used across the rest of
  // the API — see UserRole in services/users.ts), not a plain string.
  role: AuthUserRole | null
  [key: string]: unknown
}

interface AuthState {
  user: AuthUser | null
  token: string | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: getStoredToken(),
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    // Exposes just the role name (e.g. "admin") so every consumer can keep comparing it as a
    // plain string regardless of the underlying `{ id, name }` object shape.
    role: (state) => state.user?.role?.name ?? null,
  },

  actions: {
    async login(email: string, password: string) {
      const { data } = await http.post<{ user: AuthUser; token: string }>('/login', { email, password })

      this.token = data.token
      this.user = data.user
      setStoredToken(data.token)

      return data.user
    },

    async fetchMe() {
      const { data } = await http.get<AuthUser>('/me')
      this.user = data
      return data
    },

    // Resets in-memory auth state + localStorage without hitting the API — used by logout()
    // below (after its own /logout call settles) and, more importantly, by http.ts's 401
    // interceptor. That interceptor used to call clearStoredToken() alone, which only cleared
    // localStorage: this store's own `token` stayed set in memory, so `isAuthenticated` stayed
    // true and the router guard's "already authenticated" rule bounced straight back to
    // /dashboard on the very redirect meant to send the user to /login — whose own now-401ing
    // calls re-triggered the interceptor, looping. Calling the full logout() action from the
    // interceptor isn't an option either: it would fire another /logout request with the same
    // bad token, which would itself 401 and re-enter the interceptor recursively.
    clearSession() {
      this.token = null
      this.user = null
      clearStoredToken()
    },

    async logout() {
      try {
        if (this.token) {
          await http.post('/logout')
        }
      } finally {
        this.clearSession()
      }
    },
  },
})
